import type { LandingBlock, LandingRemoteBlock } from './types'

/*
  payload is a JSON scalar: Apollo may hand it back already parsed, or the
  backend may have stored it as a JSON string. Be tolerant and never throw —
  an unparsable payload degrades to an empty block, not to a broken page.
*/
export function parseLandingPayload (
  payload: LandingRemoteBlock['payload'],
): Record<string, unknown> | null {
  if (!payload) {
    return null
  }

  if (typeof payload !== 'string') {
    return payload
  }

  try {
    return JSON.parse(payload)
  }
  catch {
    return null
  }
}

/*
  State-aware visibility: a block without visibleForStates is general, otherwise
  it is shown only when the resolved landingPage.stateId is listed.
*/
export function isBlockVisible (
  block: LandingRemoteBlock,
  stateId?: string | null,
): boolean {
  const states = block?.visibleForStates

  if (!states || states.length === 0) {
    return true
  }

  return states.map(String).includes(String(stateId))
}

/*
  Blocks come ordered by `order`, but the field is optional — sort defensively
  and keep the received order for blocks that don't declare one.
*/
export function prepareLandingBlocks (
  blocks?: LandingRemoteBlock[] | null,
  stateId?: string | null,
): LandingBlock[] {
  return (blocks ?? [])
    .filter((block) => Boolean(block) && isBlockVisible(block, stateId))
    .map((block, index) => ({
      ...block,
      order: typeof block.order === 'number' ? block.order : index,
      payload: parseLandingPayload(block.payload),
    }))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
}
